"use client";

import { useRef, useState, type ReactNode } from "react";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { cn } from "@/lib/utils";

interface HeroHighlightProps {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
}

export function HeroHighlight({
  children,
  className,
  containerClassName,
}: HeroHighlightProps) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const { left, top } = containerRef.current.getBoundingClientRect();
    mouseX.set(e.clientX - left);
    mouseY.set(e.clientY - top);
  };

  const dotPattern = useMotionTemplate`
    radial-gradient(
      200px circle at ${mouseX}px ${mouseY}px,
      var(--primary) 0%,
      transparent 80%
    )
  `;

  return (
    <div
      ref={containerRef}
      className={cn("group relative overflow-hidden", containerClassName)}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Static dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle, oklch(0.55 0.15 245 / 30%) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      {/* Mouse-following highlight */}
      <motion.div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300"
        style={{
          background: dotPattern,
          opacity: isHovering ? 0.15 : 0,
          WebkitMaskImage:
            "radial-gradient(circle, oklch(0.55 0.15 245 / 40%) 1px, transparent 1px)",
          maskImage:
            "radial-gradient(circle, oklch(0.55 0.15 245 / 40%) 1px, transparent 1px)",
          WebkitMaskSize: "24px 24px",
          maskSize: "24px 24px",
        }}
      />
      <div className={cn("relative z-10", className)}>{children}</div>
    </div>
  );
}

interface HighlightProps {
  children: ReactNode;
  className?: string;
}

export function Highlight({ children, className }: HighlightProps) {
  return (
    <motion.span
      initial={{ backgroundSize: "0% 100%" }}
      whileInView={{ backgroundSize: "100% 100%" }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
      className={cn(
        "relative inline-block rounded-sm bg-gradient-to-r from-primary/30 to-accent/30 bg-no-repeat bg-left-bottom px-1 pb-1",
        className
      )}
    >
      {children}
    </motion.span>
  );
}
